import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Bookmark,
  BookmarkPlus,
  CalendarDays,
  ChevronRight,
  Moon,
  Search,
  Store as StoreIcon,
  Sun,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useTheme } from '@/auth/ThemeProvider';
import { useFilters } from '@/state/filters';
import { useStoresList } from '@/api/legacy/stores';
import { applyViewToUrl, useSavedViews } from '@/state/savedViews';
import { useVisibleNav } from '@/features/nav';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

interface CommandItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon: React.ComponentType<{ className?: string }>;
  keywords?: string[];
  onSelect: () => void;
}

interface PaletteContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
  toggle: () => void;
}

const PaletteContext = createContext<PaletteContextValue | null>(null);

/**
 * Command palette provider.
 *
 * Mount once at the app root. Children can call `useCommandPalette().toggle()`
 * to open from a button (e.g. the Topbar trigger). Globally listens for
 * ⌘K / Ctrl-K and `/` (when not in an input).
 *
 * Animation notes (Emil): the dialog uses `transform-origin: center` (modal
 * exception) and a strong custom curve, both inherited from `<Dialog>`.
 * The list itself doesn't animate row entry — we want it to feel snappy.
 */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // ⌘K / Ctrl-K and `/` shortcut — bypassed inside text fields.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      const slash = e.key === '/' && !isTypingTarget(e.target as Element | null);
      if (cmdK || slash) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <PaletteContext.Provider value={{ open, setOpen, toggle }}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </PaletteContext.Provider>
  );
}

export function useCommandPalette(): PaletteContextValue {
  const ctx = useContext(PaletteContext);
  if (!ctx) throw new Error('useCommandPalette must be used inside <CommandPaletteProvider>');
  return ctx;
}

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return Boolean((el as HTMLElement).isContentEditable);
}

function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const setPreset = useFilters((s) => s.setPreset);
  const setStoreId = useFilters((s) => s.setStoreId);
  const { theme, toggle: toggleTheme } = useTheme();
  const { groups: navGroups } = useVisibleNav();
  const { data: stores = [] } = useStoresList();
  const saveCurrent = useSavedViews((s) => s.saveCurrent);
  const views = useSavedViews(useShallow((s) => s.views));
  const savedViews = useMemo(() => {
    const list = [...views];
    list.sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
      return b.updated_at.localeCompare(a.updated_at);
    });
    return list;
  }, [views]);

  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const items = useMemo<CommandItem[]>(() => {
    // Derived from the nav registry so the palette can never fall behind the
    // sidebar — it used to list nine hand-picked routes out of ~50.
    const nav: CommandItem[] = navGroups.flatMap((group) =>
      group.items.map((item) => ({
        id: `nav:${item.path}`,
        label: item.label,
        hint: item.external ? 'new tab' : undefined,
        group: group.label,
        icon: item.icon,
        keywords: [group.label.toLowerCase(), item.path],
        onSelect: () => {
          if (item.external) window.open(item.path, '_blank', 'noopener');
          else navigate(item.path);
        },
      })),
    );

    const ranges: CommandItem[] = [
      { id: 'r:today', label: 'Today', group: 'Date range', icon: CalendarDays, onSelect: () => setPreset('today') },
      { id: 'r:yesterday', label: 'Yesterday', group: 'Date range', icon: CalendarDays, onSelect: () => setPreset('yesterday') },
      { id: 'r:7d', label: 'Last 7 days', group: 'Date range', icon: CalendarDays, onSelect: () => setPreset('7d') },
      { id: 'r:30d', label: 'Last 30 days', group: 'Date range', icon: CalendarDays, onSelect: () => setPreset('30d') },
      { id: 'r:mtd', label: 'Month to date', group: 'Date range', icon: CalendarDays, onSelect: () => setPreset('mtd') },
    ];

    const storesMenu: CommandItem[] = [
      { id: 'store:all', label: 'All stores', group: 'Filter store', icon: StoreIcon, onSelect: () => setStoreId(null) },
      ...stores.map((s) => ({
        id: `store:${s.id}`,
        label: s.name,
        hint: s.is_online ? 'online' : 'offline',
        group: 'Filter store',
        icon: StoreIcon,
        keywords: ['store', s.name.toLowerCase()],
        onSelect: () => setStoreId(s.id),
      })),
    ];

    const themeMenu: CommandItem[] = [
      {
        id: 'theme:toggle',
        label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Appearance',
        icon: theme === 'dark' ? Sun : Moon,
        onSelect: toggleTheme,
      },
    ];

    const viewsMenu: CommandItem[] = [
      {
        id: 'view:save',
        label: 'Save current view…',
        hint: 'name it',
        group: 'Saved views',
        icon: BookmarkPlus,
        keywords: ['bookmark', 'pin', 'preset'],
        onSelect: () => {
          // Defer the prompt so the palette can close cleanly first.
          requestAnimationFrame(() => {
            const name = window.prompt('Name this view (e.g. "Today · Pulchowk store")');
            if (!name?.trim()) return;
            const v = saveCurrent(name);
            notify.success(`Saved "${v.name}"`);
          });
        },
      },
      ...savedViews.map((v) => ({
        id: `view:${v.id}`,
        label: v.name,
        hint: v.pinned ? 'pinned' : undefined,
        group: 'Saved views',
        icon: Bookmark,
        keywords: ['view', 'bookmark', v.name.toLowerCase()],
        onSelect: () => applyViewToUrl(v, navigate, location.pathname),
      })),
    ];

    return [...nav, ...ranges, ...storesMenu, ...viewsMenu, ...themeMenu];
  }, [navigate, location.pathname, setPreset, setStoreId, stores, theme, toggleTheme, savedViews, saveCurrent, navGroups]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) => {
      const hay = `${i.label} ${i.group} ${(i.keywords ?? []).join(' ')}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [items, query]);

  // Group preserving insertion order from `items`.
  const grouped = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    filtered.forEach((it) => {
      const arr = map.get(it.group) ?? [];
      arr.push(it);
      map.set(it.group, arr);
    });
    return Array.from(map.entries());
  }, [filtered]);

  // Reset on each open + auto-focus the search field.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIdx]);

  // Scroll the active row into view (cheap).
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  function commit(item: CommandItem) {
    item.onSelect();
    close();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = filtered[activeIdx];
      if (it) commit(it);
    } else if (e.key === 'Escape') {
      close();
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'duration-150 ease-out',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-[18vh] z-[1010] w-full max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-kpi',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-200 ease-out',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Jump anywhere — pages, dates, stores, theme…"
              className="h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <kbd className="hidden rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
              esc
            </kbd>
          </div>

          <div ref={listRef} className="max-h-[55vh] overflow-y-auto p-1">
            {grouped.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                Nothing matches <span className="font-medium text-foreground">"{query}"</span>.
              </div>
            ) : (
              grouped.map(([group, list]) => (
                <div key={group} className="mb-1">
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {group}
                  </div>
                  {list.map((it) => {
                    const Icon = it.icon;
                    const idx = filtered.indexOf(it);
                    const active = idx === activeIdx;
                    return (
                      <button
                        type="button"
                        key={it.id}
                        data-cmd-idx={idx}
                        onClick={() => commit(it)}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={cn(
                          'press flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors duration-100 ease-out focus-ring',
                          active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{it.label}</span>
                        {it.hint ? (
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
                            {it.hint}
                          </span>
                        ) : null}
                        {active ? <ChevronRight className="h-3.5 w-3.5 opacity-70" aria-hidden /> : null}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-1.5 text-[10px] text-muted-foreground">
            <span className="font-mono">↑↓ navigate · ↵ select</span>
            <span>
              Tip: hit <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono text-[10px]">/</kbd> anywhere
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
